/**
 * ContactTypesManager Component
 *
 * Admin component to manage CRM contact types in Supabase (crm_contact_types).
 * Supports drag-and-drop reordering. Tags are managed in the dedicated Tags Manager.
 *
 * Architecture:
 * - Contact types stored in Supabase crm_contact_types table
 * - Real-time updates via CrmSettingsContext subscription
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select as ChakraSelect,
  useToast,
  useColorModeValue,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMenu,
} from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { ReactSortable } from 'react-sortablejs';

const AVAILABLE_COLORS = [
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'teal', label: 'Teal' },
  { value: 'blue', label: 'Blue' },
  { value: 'cyan', label: 'Cyan' },
  { value: 'purple', label: 'Purple' },
  { value: 'pink', label: 'Pink' },
];

const AVAILABLE_ICONS = [
  'FiUser', 'FiHome', 'FiFileText', 'FiDollarSign', 'FiBriefcase',
  'FiHeart', 'FiTool', 'FiStar', 'FiAward', 'FiCircle',
];

export default function ContactTypesManager() {
  const toast = useToast();
  const { contactTypes, contactTypesLoading, refreshContactTypes } = useCrmSettings();

  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localTypes, setLocalTypes] = useState([]);
  const [orderChanged, setOrderChanged] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const dragHandleBG = useColorModeValue('gray.100', 'gray.600');

  useEffect(() => {
    if (contactTypes && contactTypes.length > 0) {
      setLocalTypes(contactTypes.map(t => ({
        ...t,
        id: t.id || t.value,
      })));
      setOrderChanged(false);
    }
  }, [contactTypes]);

  if (!isSupabaseConfigured()) {
    return (
      <Alert status="warning">
        <AlertIcon />
        Supabase is not configured. Contact types cannot be managed.
      </Alert>
    );
  }

  const handleAdd = () => {
    setSelectedType(null);
    onOpen();
  };

  const handleEdit = (type) => {
    setSelectedType(type);
    onOpen();
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Are you sure you want to delete the contact type "${type.label}"?`)) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('crm_contact_types')
        .update({ is_active: false })
        .eq('value', type.value);

      if (error) throw error;

      toast({
        title: 'Contact type deleted',
        status: 'success',
        duration: 2000,
      });

      refreshContactTypes();
    } catch (error) {
      console.error('Error deleting contact type:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = (newList) => {
    setLocalTypes(newList);
    const originalOrder = (contactTypes || []).map(t => t.value).join(',');
    const newOrder = newList.map(t => t.value).join(',');
    setOrderChanged(originalOrder !== newOrder);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < localTypes.length; i++) {
        const { error } = await supabase
          .from('crm_contact_types')
          .update({ sort_order: i })
          .eq('value', localTypes[i].value);

        if (error) throw error;
      }

      toast({
        title: 'Order saved',
        description: 'Contact type order has been updated',
        status: 'success',
        duration: 2000,
      });

      setOrderChanged(false);
      refreshContactTypes();
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Contact Types</Heading>
            <Text fontSize="sm" color="gray.600">
              Manage contact types (e.g. Borrower, Real Estate Agent)
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            size="sm"
            onClick={handleAdd}
          >
            Add Contact Type
          </Button>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Drag types to reorder. Changes affect contact type dropdowns and filters.
          </Text>
        </Alert>

        {orderChanged && (
          <Flex justify="flex-end">
            <Button
              colorScheme="green"
              size="sm"
              onClick={saveOrder}
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Order
            </Button>
          </Flex>
        )}

        {contactTypesLoading ? (
          <Center py={8}>
            <Spinner />
          </Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={tableHeaderBG}>
                <Tr>
                  <Th w="40px"></Th>
                  <Th>Preview</Th>
                  <Th>Value (ID)</Th>
                  <Th>Label</Th>
                  <Th>Color</Th>
                  <Th>Icon</Th>
                  <Th>Description</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <ReactSortable
                tag={Tbody}
                list={localTypes}
                setList={handleReorder}
                animation={200}
                handle=".drag-handle"
              >
                {localTypes.map((type) => (
                  <Tr key={type.value} _hover={{ bg: hoverBG }}>
                    <Td p={1}>
                      <IconButton
                        icon={<FiMenu />}
                        size="xs"
                        variant="ghost"
                        aria-label="Drag to reorder"
                        className="drag-handle"
                        cursor="grab"
                        _hover={{ bg: dragHandleBG }}
                      />
                    </Td>
                    <Td>
                      <Badge colorScheme={type.color}>{type.label}</Badge>
                    </Td>
                    <Td fontFamily="mono" fontSize="xs">{type.value}</Td>
                    <Td fontWeight="medium">{type.label}</Td>
                    <Td>
                      <Badge colorScheme={type.color} variant="solid">
                        {type.color}
                      </Badge>
                    </Td>
                    <Td fontSize="xs">{type.icon || '—'}</Td>
                    <Td fontSize="xs" noOfLines={1} maxW="200px">{type.description || '—'}</Td>
                    <Td>
                      <HStack spacing={1}>
                        <IconButton
                          icon={<FiEdit2 />}
                          size="xs"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={() => handleEdit(type)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Delete"
                          onClick={() => handleDelete(type)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </ReactSortable>
            </Table>
          </Box>
        )}
      </VStack>

      <EditorModal
        isOpen={isOpen}
        onClose={onClose}
        type={selectedType}
        existingTypes={contactTypes || []}
        onSave={refreshContactTypes}
      />
    </Box>
  );
}

function EditorModal({ isOpen, onClose, type, existingTypes, onSave }) {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('blue');
  const [icon, setIcon] = useState('FiUser');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (type) {
      setValue(type.value || '');
      setLabel(type.label || '');
      setColor(type.color || 'blue');
      setIcon(type.icon || 'FiUser');
      setDescription(type.description || '');
    } else {
      setValue('');
      setLabel('');
      setColor('blue');
      setIcon('FiUser');
      setDescription('');
    }
  }, [type, isOpen]);

  const handleSave = async () => {
    if (!value.trim() || !label.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Value and label are required',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!/^[a-z0-9-]+$/.test(value)) {
      toast({
        title: 'Invalid value format',
        description: 'Value must be lowercase alphanumeric with hyphens only',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      if (type) {
        const { error } = await supabase
          .from('crm_contact_types')
          .update({
            label: label.trim(),
            color,
            icon: icon || null,
            description: description.trim() || null,
          })
          .eq('value', type.value);

        if (error) throw error;

        toast({
          title: 'Contact type updated',
          status: 'success',
          duration: 2000,
        });
      } else {
        if (existingTypes.some(t => t.value === value.trim())) {
          toast({
            title: 'Duplicate value',
            description: 'A contact type with this value already exists',
            status: 'error',
            duration: 3000,
          });
          setIsSaving(false);
          return;
        }

        const maxSortOrder = Math.max(-1, ...(existingTypes.map(t => t.sortOrder ?? t.sort_order ?? -1)));
        const sort_order = maxSortOrder + 1;

        const { error } = await supabase
          .from('crm_contact_types')
          .insert({
            value: value.trim(),
            label: label.trim(),
            color,
            icon: icon || null,
            description: description.trim() || null,
            sort_order,
            is_active: true,
          });

        if (error) throw error;

        toast({
          title: 'Contact type created',
          status: 'success',
          duration: 2000,
        });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving contact type:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {type ? 'Edit' : 'Create'} Contact Type
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Value (Unique ID)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., real-estate-agent"
                isDisabled={!!type}
              />
              <Text fontSize="xs" color="gray.600" mt={1}>
                Lowercase, alphanumeric, hyphens only. Cannot be changed after creation.
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Display Label</FormLabel>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Real Estate Agent"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Color</FormLabel>
              <ChakraSelect
                value={color}
                onChange={(e) => setColor(e.target.value)}
              >
                {AVAILABLE_COLORS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </ChakraSelect>
              <HStack spacing={2} mt={2}>
                <Text fontSize="xs">Preview:</Text>
                <Badge colorScheme={color}>{label || 'Sample'}</Badge>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Icon (Optional)</FormLabel>
              <ChakraSelect
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
              >
                <option value="">No Icon</option>
                {AVAILABLE_ICONS.map(i => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </ChakraSelect>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Description (Optional)</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe when to use this contact type..."
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            {type ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
